import { Injectable } from '@angular/core';
import { CapitalizePipe } from 'src/app/core/pipes/capitalize.pipe';
import { TvdDrillInMetricTiles } from '../types/tvd-types';
import { eroiService } from '../services/eroi-metric.service';

export enum KpiCardLayout {
  Standard,
  Pest
}

export interface KpiCardBuilderParams {
  layout: KpiCardLayout;

  metricId: string;
  metricName: string;

  tvdDollars: number;
  modifiedTVDDollars: number;

  hasCostAssumption: boolean;

  tvdMetricQuantity : number;
  modifiedTvdMetricQuantity: number;

  totalLocations: string;
  totalContributingProducts: number | string | null;

  avgTVDMetricDollars?: number;
  modifiedAvgTVDMetricDollars: number;
  avgTVDMetricQuantity: number;
  avgMetricUnits: string;

  currencySymbol: string;

  hasTwoMetrics: boolean;
  secondaryMetricSum: number;
  rate?: string | number;
  defaultRate?: string | number;

  costAssumptionValue2: number;
}

export interface StandardKpiCardBuilderParams extends KpiCardBuilderParams {
}

export interface PestKpiCardBuilderParams extends KpiCardBuilderParams {
}

@Injectable({
  providedIn: 'root'
})
export class TvdKpiCardBuilder {

  // No dollar value metrics which should show N/A in drilldowns
  private readonly noDollarValueMetrics: string[] = [
    'Employee Safety',
    'Greenhouse Gases',
    'CO2e from Energy Use',
    'CO2e from Transportation',
    'CO2e from Packaging Manufacture'
  ];

  constructor(
    private readonly _eroiService: eroiService,
    private readonly _capitalizePipe: CapitalizePipe
  ) {}

  public build(
    params: KpiCardBuilderParams,
    highlightModifiedValues = false
  ): TvdDrillInMetricTiles[] {
    const isNoDollarValueMetric = this.noDollarValueMetrics.includes(params.metricName);
    const metricUnits = this._eroiService.mapIdToUnits(params.metricId);

    if (params.layout === KpiCardLayout.Pest) {
      return this.buildPestKpiCards(params as PestKpiCardBuilderParams, highlightModifiedValues, isNoDollarValueMetric, metricUnits, params.hasCostAssumption);
    }

    return this.buildStandardCards(params as StandardKpiCardBuilderParams, highlightModifiedValues, isNoDollarValueMetric, metricUnits, params.hasCostAssumption);
  }

  private buildStandardCards(
    params: StandardKpiCardBuilderParams,
    highlightModifiedValues: boolean,
    isNoDollarValueMetric: boolean,
    metricUnits: string,
    hasCostAssumption: boolean
  ): TvdDrillInMetricTiles[] {
    const metricNameCapitalized = this._capitalizePipe.transform(params.metricName);
    const capitalizedMetricUnits = this._capitalizePipe.transform(metricUnits);

    return [
      {
        id: 'estimated-tvd-metric',
        title: 'Estimated Total Value',
        originalValue: this.getOriginalValue(params.tvdDollars, isNoDollarValueMetric, hasCostAssumption),
        showDefaultValue: hasCostAssumption,
        isCurrency: !isNoDollarValueMetric,
        modifiedValue: this.getDisplayValue(params.modifiedTVDDollars, isNoDollarValueMetric),
        previewCssClass: highlightModifiedValues
          ? 'text-neptune-default'
          : '',
        isModifiedValueHighlighted: highlightModifiedValues
      },
      {
        id: 'tvd-metric-units',
        title: capitalizedMetricUnits ?? metricUnits,
        modifiedValue: this.getDisplayValue(params.modifiedTvdMetricQuantity, params.metricName === 'Cost Avoidance'),
        isCurrency: false,
        originalValue: '',
        previewCssClass: '',
        isModifiedValueHighlighted: false
      },
      {
        id: 'tvd-total-locations',
        title: 'Locations',
        modifiedValue: params.totalLocations,
        isCurrency: false,
        originalValue: '',
        previewCssClass: '',
        isModifiedValueHighlighted: false
      },
      {
        id: 'tvd-total-contributing-products',
        title: '# Contributing Products',
        modifiedValue: params.totalContributingProducts,
        isCurrency: false,
        originalValue: '',
        previewCssClass: '',
        isModifiedValueHighlighted: false
      },
      {
        id: 'tvd-avg-metric-per-location',
        title: `Average ${metricNameCapitalized} ${params.currencySymbol}/Location`,
        originalValue: this.getOriginalValue(params.avgTVDMetricDollars, isNoDollarValueMetric, hasCostAssumption),
        showDefaultValue: hasCostAssumption,
        isCurrency: !isNoDollarValueMetric,
        modifiedValue: this.getDisplayValue(params.modifiedAvgTVDMetricDollars, isNoDollarValueMetric),
        previewCssClass: highlightModifiedValues
          ? 'text-neptune-default'
          : '',
        isModifiedValueHighlighted: highlightModifiedValues
      },
      {
        id: 'tvd-avg-metric-units-per-location',
        title: `Average ${capitalizedMetricUnits}/Locations`,
        modifiedValue: this.getDisplayValue(params.avgTVDMetricQuantity, params.metricName === 'Cost Avoidance'),
        isCurrency: false,
        originalValue: '',
        previewCssClass: '',
        isModifiedValueHighlighted: false
      }
    ];
  }

  private buildPestKpiCards(
    params: PestKpiCardBuilderParams,
    highlightModifiedValues: boolean,
    isNoDollarValueMetric: boolean,
    metricUnits: string,
    hasCostAssumption: boolean
  ): TvdDrillInMetricTiles[] {

    const metricUnitsCapitalized = this._capitalizePipe.transform(metricUnits);

    const cards: TvdDrillInMetricTiles[] = [
      {
        id: 'estimated-tvd-metric',
        title: 'Estimated Total Value',
        modifiedValue: this.getDisplayValue(params.modifiedTVDDollars, isNoDollarValueMetric),
        isCurrency: !isNoDollarValueMetric,
        originalValue: this.getOriginalValue(params.tvdDollars, isNoDollarValueMetric, hasCostAssumption),
        showDefaultValue: hasCostAssumption,
        previewCssClass: highlightModifiedValues
          ? 'text-neptune-default'
          : '',
        isModifiedValueHighlighted: highlightModifiedValues
      },
      {
        id: 'tvd-metric-units',
        title: metricUnitsCapitalized,
        modifiedValue: params.modifiedTvdMetricQuantity,
        isCurrency: false,
        // This is misleading because the original value (params.tvdMetricQuantity) is not actually zero. 
        // If a cost assumption is applied that affects the estimated-tvd-metric (above), 
        // but not the tvd-metric-units, then params.tvdMetricQuantity will be zero.
        // In this case, do not display the default value for the KPI to avoid showing "Default - 0" in the UI.
        originalValue: params.tvdMetricQuantity == 0 ? '' : this.getOriginalValue(params.tvdMetricQuantity, isNoDollarValueMetric, hasCostAssumption),
        // Generally for most metrics, only display the default if the value has actually changed.
        // For the future, there needs to be a stronger check for if the metrics non-dollar value can be/was updated.
        // Might have to refactor the cost assumptions logic for displaying defaults in the UI for clarity.
        showDefaultValue: hasCostAssumption && params.tvdMetricQuantity !== params.modifiedTvdMetricQuantity,
        previewCssClass: hasCostAssumption && params.tvdMetricQuantity !== params.modifiedTvdMetricQuantity
          ? 'text-neptune-default'
          : '',
        isModifiedValueHighlighted: highlightModifiedValues
      },
      {
        id: 'tvd-total-locations',
        title: 'Locations',
        modifiedValue: params.totalLocations,
        isCurrency: false,
        originalValue: '',
        previewCssClass: '',
        isModifiedValueHighlighted: false
      },
      {
        id: 'tvd-avg-metric-units',
        title: params.avgMetricUnits,
        modifiedValue: this.getDisplayValue(params.modifiedAvgTVDMetricDollars, isNoDollarValueMetric),
        isCurrency: !isNoDollarValueMetric,
        originalValue: this.getOriginalValue(params.avgTVDMetricDollars, isNoDollarValueMetric, hasCostAssumption),
        showDefaultValue: hasCostAssumption,
        previewCssClass: highlightModifiedValues
          ? 'text-neptune-default'
          : '',
        isModifiedValueHighlighted: highlightModifiedValues
      }
    ];

    if (params.metricName === 'Set-up Services') {
      cards.splice(4, 0, {
        id: 'setup-services-co-multiplier',
        title: 'C/O Multiplier',
        modifiedValue: params.rate ?? '',
        isCurrency: false,
        originalValue: this.getOriginalValue(params.defaultRate, isNoDollarValueMetric, hasCostAssumption),
        showDefaultValue: hasCostAssumption,
        previewCssClass: highlightModifiedValues
          ? 'text-neptune-default'
          : '',
        isModifiedValueHighlighted: highlightModifiedValues
      });
    }

    if (params.metricName === 'No Charge Additional Services') {
      cards.splice(4, 0, {
        id: 'no-charge-additional-services-rodent-multiplier',
        title: 'Rodent Service Multiplier',
        modifiedValue: params.rate ?? '',
        isCurrency: false,
        originalValue: this.getOriginalValue(params.defaultRate, isNoDollarValueMetric, hasCostAssumption),
        showDefaultValue: hasCostAssumption,
        previewCssClass: highlightModifiedValues
          ? 'text-neptune-default'
          : '',
        isModifiedValueHighlighted: highlightModifiedValues
      });
    }

    if (params.hasTwoMetrics) {
      const title =
        params.metricName === 'No Charge Additional Services'
          ? 'Total # of Rodent No Charge Services'
          : 'Total $ Value Invoiced';

      cards.splice(4, 0, {
        id: 'second-avg-metric-per-location',
        title,
        modifiedValue: params.secondaryMetricSum,
        isCurrency: false,
        originalValue: '',
        previewCssClass: '',
        isModifiedValueHighlighted: false
      });
    }

    if (params.metricName === 'Room Nights Saved') {
      let metricUnitsCard = cards.find(card => card.id === 'tvd-metric-units');
      if(metricUnitsCard)
      {
        // Business Logic: If the user has entered a cost assumption into the second cost assumption form (# of Days saved),
        // then display the default value for the tvd-metric-units card, even if the default value is the same as the modified/effective value.
        metricUnitsCard.showDefaultValue = hasCostAssumption && params.costAssumptionValue2 != 0,
        metricUnitsCard.previewCssClass = highlightModifiedValues
        ? 'text-neptune-default' 
        : '';
      }
      
      cards.splice(4, 0, {
        id: 'room-nights-saved-number-of-days',
        title: '# of Days',
        modifiedValue: params.rate ?? '',
        isCurrency: false,
        originalValue: this.getOriginalValue(params.defaultRate, isNoDollarValueMetric, hasCostAssumption),
        // Business Logic: If the user has entered a cost assumption into the second cost assumption form (# of Days saved),
        // then display the default value for the room-nights-saved-number-of-days card, even if the default value is the same as the modified/effective value.
        showDefaultValue: hasCostAssumption && params.costAssumptionValue2 != 0,
        previewCssClass: highlightModifiedValues
          ? 'text-neptune-default'
          : '',
        isModifiedValueHighlighted: highlightModifiedValues
      });
    }

    return cards;
  }

  private getDisplayValue<T>(
    value: T,
    isNoDollarValueMetric: boolean
  ): T | 'N/A' {
    return isNoDollarValueMetric
      ? 'N/A'
      : value;
  }

  private getOriginalValue<T>(
    value: T,
    isNoDollarValueMetric: boolean,   
    hasCostAssumption: boolean
  ): T | 'N/A' | '' {
    if (!hasCostAssumption) {
      return '';
    }

    return this.getDisplayValue(
      value,
      isNoDollarValueMetric
    );
  }

  /**
   * 
   * @param metric The TVD drill-in metric kpi tile to evaluate.
   * @returns A boolean indicating whether the default value should be shown in the UI for the given kpi tile.
   */
  public shouldShowDefaultValueForKpiCard(metric: TvdDrillInMetricTiles): boolean {
    if (metric.originalValue === null || metric.originalValue === '') {
      return false;
    }

    if (metric.modifiedValue === 'N/A' || metric.modifiedValue === null) {
      return false;
    }

    return metric.showDefaultValue === true || metric.originalValue !== metric.modifiedValue;
  }

}